/* eslint-disable functional/prefer-readonly-type */
/* eslint-disable max-lines-per-function */
import React, { ChangeEvent } from 'react'
import { State, useHookstate } from '@hookstate/core'
import {
  Button,
  Collapse,
  Grid,
  IconButton,
  InputAdornment,
  OutlinedInput,
  TextField,
} from '@mui/material'
import { useLang } from '~/hooks/useLang'
import FilterSelectPeriod from '~/components/FilterAutocompletePeriod'
import FilterAutocompleteAgency from '~/components/FilterAutocompleteAgency'
import { Dayjs } from 'dayjs'
import FilterAutocompleteDispatchsGroups from '~/components/FilterAutocompleteDispatchsGroups'
import FilterAutocompleteDevice from '~/components/FilterAutocompleteDevice'
import FilterSwitchCoordination from '~/components/FilterSwitchCoordination'
import { CurrentCoordination } from '~/components/FilterSwitchCoordination/interface'
import FilterSwitchOtherDispatchsGroup from '~/components/FilterSwitchOtherDispatchsGroups'
import FilterAutocompleteOperation from '~/components/FilterAutocompleteOperation'
import SelectConsummatedAndAttempted from '~/components/FilterSelectConsummatedAndAttempted'
import useSystemState from '~/features/System/stores/SystemState'
import { LoadingButton } from '@mui/lab'
import FilterAutocompleteDispatchGroupSet from '~/components/FilterAutocompleteDispatchGroupSet'
import FilterAutocompleteCategories from '~/components/FilterAutocompleteCategories'
import FilterAutocompleteCities from '~/components/FilterAutocompleteCities'
import Cancel from '@mui/icons-material/Cancel'
import FilterAutocompleteOperationUnit from '~/components/FilterAutocompleteOperationUnit'
import FilterAutocompleteMultipleStatus from '~/components/FilterAutocompleteMutipleStatus'
import FilterAutocompleteClosedStatusClass from '~/components/FilterAutocompleteClosedStatusClass'
import FilterAutocompleteClosedStatus from '~/components/FilterAutocompleteClosedStatus'
import FilterCheckboxQualifiers from '~/components/FilterCheckboxQualifiers'
import FilterAutoCompleteTypesWithCategory from '~/components/FilterAutoCompleteTypesWithCategory'
import FilterAutocompleteSubtypesWithCategory from '~/components/FilterAutocompleteSubtypesWithCategory'
import PhoneTextField from '~/components/PhoneField'
import FilterGeographicalArea from '~/features/Map/components/FilterGeographicalArea'
import FilterAutocompleteFetch from '~/components/FilterAutocompleteFetch'
import FilterAutocompleteDistrictsWithCity from '~/components/FilterSelectAutocompleteDistricts'
import type { IGeographical } from '~/features/Map/components/FilterGeographicalArea/interfaces'
import FilterTimeInterval from '~/components/FilterTimeInterval'

interface DispatchFiltersDashboardProps {
  readonly loading: State<boolean>
  readonly categoryIds: State<readonly number[]>
  readonly currentCoordination: State<CurrentCoordination | null>
  readonly deviceId: State<number | null>
  readonly groupId: State<readonly number[]>
  readonly agencyId: State<number | null>
  readonly initialPeriod: State<string>
  readonly typeIds: State<readonly number[]>
  readonly subtypeIds?: State<readonly number[]>
  readonly refTypeIds?: any
  readonly operationId?: State<number | null>
  readonly hidden?: boolean
  readonly lockFields: boolean
  readonly withSolvedThirdParty: State<boolean>
  readonly consummatedAndAttempted: State<string | null>
  readonly focusChangeUserId?: State<boolean>
  readonly resetValueUserId?: State<boolean>
  readonly openModalGeographicalArea: State<boolean>
  readonly userId: State<number | null>

  readonly searchButton: () => void
  readonly clearFilters: () => void
  readonly setCoordinationDates: (date: boolean) => void

  readonly onStartDateChange: (value: Dayjs | null) => void
  readonly onFinishDateChange: (value: Dayjs | null) => void
  readonly onPeriodChange: (value: string) => void

  readonly statusIds: State<readonly number[]>
  readonly cityIds: State<readonly number[]>
  readonly districtIds: State<readonly number[]>
  readonly dispatchGroupSetId: State<number | null>
  readonly location: State<string>
  readonly operationUnitId: State<number | null>
  readonly closedStatusClassId?: State<number | null>
  readonly closedStatusId?: State<number | null>
  readonly qualifiersSelected?: State<readonly number[]>
  readonly clearState?: State<boolean>
  readonly requester: State<string | null>
  readonly phone: State<string | null>
  readonly geographicalArea: State<IGeographical[]>
  readonly disabledSendButton: () => boolean
  readonly timeIntervalEnabled: State<boolean>
  readonly startTime: State<string>
  readonly endTime: State<string>
}

export default function DispatchFiltersDashboard({
  loading,
  setCoordinationDates,
  lockFields,
  consummatedAndAttempted,
  currentCoordination,
  withSolvedThirdParty,
  deviceId,
  groupId,
  agencyId,
  initialPeriod,
  onFinishDateChange,
  onPeriodChange,
  onStartDateChange,
  typeIds,
  subtypeIds,
  refTypeIds,
  categoryIds,
  operationId,
  hidden,
  clearFilters,
  searchButton,
  statusIds,
  cityIds,
  dispatchGroupSetId,
  location,
  operationUnitId,
  closedStatusClassId,
  closedStatusId,
  qualifiersSelected,
  clearState,
  phone,
  openModalGeographicalArea,
  requester,
  userId,
  focusChangeUserId,
  resetValueUserId,
  districtIds,
  geographicalArea,
  disabledSendButton,
  timeIntervalEnabled,
  startTime,
  endTime,
}: DispatchFiltersDashboardProps) {
  const { translate } = useLang()
  const { systemConfig } = useSystemState()
  const itemTypeRemoved = useHookstate(false)
  const itemSubtypeRemoved = useHookstate(false)
  const closedStatusClassLock = lockFields
    ? lockFields
    : !!statusIds.get()?.length && !statusIds.get()?.includes(8)

  return (
    <Collapse in={hidden}>
      <Grid
        container
        xs={12}
        sx={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          paddingX: 2,
          paddingBottom: 2,
        }}
      >
        <Grid container direction={'row'}>
          <Grid item xs={2} sx={{ pl: 1, pt: 1 }}>
            <FilterAutocompleteAgency
              disabled={lockFields}
              agencyId={agencyId.get()}
              onAgencyChange={(agency) => {
                agencyId.set(agency)
                groupId.set([])
              }}
            />
          </Grid>
          <Grid item xs={2} sx={{ pl: 1, pt: 1 }}>
            <FilterAutocompleteDispatchGroupSet
              disabled={lockFields}
              agencyId={agencyId.get() ?? undefined}
              dispatchGroupSetId={dispatchGroupSetId.get() ?? undefined}
              onGroupDispatchSetChange={(group) => {
                dispatchGroupSetId.set(group)
              }}
            />
          </Grid>
          <Grid item xs={2} sx={{ pl: 1, pt: 1 }}>
            <FilterAutocompleteDispatchsGroups
              disabled={lockFields}
              agencyId={agencyId.get() ?? undefined}
              dispatcheGroups={groupId.get() ?? undefined}
              onGroupDispatchChange={(group) => {
                groupId.set(group)
                deviceId.set(null)
              }}
              groupSetId={dispatchGroupSetId?.get()}
              filterGroupVisibles={true}
            />
          </Grid>
          <Grid item xs={2} sx={{ pl: 1, pt: 1 }}>
            <FilterAutocompleteCategories
              disabled={lockFields}
              categoriesId={categoryIds.get()}
              onCategoriesChange={(categories) => {
                categoryIds.set(categories)
                // typeIds.set([])
              }}
            />
          </Grid>
          <Grid item xs={2} sx={{ pl: 1, pt: 1 }}>
            {/* <FilterAutocompleteTypes
              disabled={lockFields}
              categoryId={categoryIds.get() ?? undefined}
              typeId={typeIds.get()}
              onTypeChange={(type) => {
                typeIds.set(type)
                !type.length && subtypeIds?.set([])
              }}
            /> */}
            <FilterAutoCompleteTypesWithCategory
              disabled={lockFields}
              categoryId={categoryIds.get()}
              typeId={typeIds.get()}
              subtypes={subtypeIds?.get() ?? []}
              refTypeIds={refTypeIds}
              itemTypeRemoved={itemTypeRemoved}
              itemSubtypeRemoved={itemSubtypeRemoved}
              onTypeChange={(type) => {
                typeIds.set(type)
              }}
              onSubtypesChange={(subtype) => {
                subtypeIds?.set(subtype)
              }}
            />
          </Grid>
          <Grid item xs={2} sx={{ pl: 1, pt: 1 }}>
            <FilterAutocompleteSubtypesWithCategory
              disabled={lockFields}
              subtypes={subtypeIds?.get() ?? []}
              typeId={typeIds.get()}
              categoryIds={categoryIds.get()}
              refTypeIds={refTypeIds}
              itemTypeRemoved={itemTypeRemoved}
              itemSubtypeRemoved={itemSubtypeRemoved}
              onTypeChange={(refId) => {
                typeIds.set(refId)
              }}
              onSubtypesChange={(subtype) => {
                subtypeIds?.set(subtype)
              }}
            />
            {/* <FilterAutocompleteSubtypesByTypes
              disabled={lockFields}
              subtypes={subtypeIds?.get() ?? []}
              typeId={typeIds.get()}
              onSubtypesChange={(subtype) => subtypeIds?.set(subtype)}
              /> 
              */}
          </Grid>
          <Grid item xs={2} sx={{ pl: 1, pt: 1 }}>
            <FilterAutocompleteCities
              disabled={lockFields}
              onCitiesChange={(city) => {
                cityIds.set(city)
              }}
              citiesId={cityIds.get()}
            />
          </Grid>
          <Grid item xs={2} sx={{ pl: 1, pt: 1 }}>
            <FilterAutocompleteDistrictsWithCity
              disabled={lockFields}
              onDistrictsChange={(districts) => {
                districtIds.set(districts ?? [])
              }}
              onCityChange={(cities) => {
                cityIds.set(cities ?? [])
              }}
              districts={districtIds.get() ?? []}
              cityId={cityIds.get()}
              limitedTags={5}
            />
          </Grid>
          <Grid item xs={2} sx={{ pl: 1, pt: 1 }}>
            <OutlinedInput
              fullWidth
              id="outlined-search"
              value={location?.get() ?? null}
              size="small"
              placeholder={translate('Location')}
              onChange={(event) => {
                location?.set(event.target.value)
              }}
              endAdornment={
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle clear search location"
                    onClick={() => {
                      location?.set('')
                    }}
                    disabled={location?.get()?.length ? false : true}
                    edge="end"
                  >
                    <Cancel fontSize="small" />
                  </IconButton>
                </InputAdornment>
              }
            />
          </Grid>
          <Grid item xs={2} sx={{ pl: 1, pt: 1 }}>
            <FilterGeographicalArea
              openModalGeographicalArea={openModalGeographicalArea}
              geographicalArea={geographicalArea}
            />
          </Grid>
          <Grid item xs={2} sx={{ pl: 1, pt: 1 }}>
            <TextField
              fullWidth
              label={translate('Requester')}
              size="small"
              value={requester?.get() ?? ''}
              onChange={(event: ChangeEvent<HTMLInputElement>) => {
                requester?.set(event.target.value)
              }}
            />
          </Grid>
          <Grid item xs={2} sx={{ pl: 1, pt: 1 }}>
            <PhoneTextField
              statePhone={phone}
              disabledLastCalls
              disabled={false}
              phoneRequesterPlaceholderLabel
            />
          </Grid>
          <Grid item xs={2} sx={{ pl: 1, pt: 1 }}>
            <FilterAutocompleteFetch
              label={'User'}
              urlFetch={'/user/get'}
              size={'small'}
              disabled={false}
              onFetchChange={(user: number | null) => userId?.set(user)}
              resetValues={resetValueUserId?.get()}
              focusChange={focusChangeUserId}
              // model='user'
              valueId={userId?.get() ? Number(userId?.get()) : undefined}
            />
          </Grid>
          <Grid item xs={2} sx={{ pl: 1, pt: 1 }}>
            <FilterAutocompleteOperation
              disabled={lockFields}
              onOperationChange={(operation) => {
                operationId?.set(operation)
              }}
              operationId={operationId?.get()}
            />
          </Grid>
          <Grid item xs={2} sx={{ pl: 1, pt: 1 }}>
            <FilterAutocompleteOperationUnit
              disabled={lockFields}
              onOperationUnitChange={(operationUnit) => {
                operationUnitId?.set(operationUnit)
                deviceId?.set(null)
              }}
              operationUnitId={operationUnitId?.get()}
            />
          </Grid>
          <Grid item xs={2} sx={{ pl: 1, pt: 1 }}>
            <FilterAutocompleteDevice
              disabled={lockFields}
              onDeviceChange={(device) => {
                deviceId.set(device)
              }}
              deviceId={deviceId.get()}
              groupDispatchs={groupId.get() ?? undefined}
              operationUnitId={operationUnitId.get()}
            />
          </Grid>
          <FilterSelectPeriod
            disabled={lockFields}
            onStartDateChange={onStartDateChange}
            onFinishDateChange={onFinishDateChange}
            onPeriodChange={onPeriodChange}
            period={initialPeriod.get()}
            periodOptions={[
              {
                id: 0,
                number: 15,
                type: 'minutes',
                name: translate('Last 15 minutes'),
              },
              {
                id: 1,
                number: 30,
                type: 'minutes',
                name: translate('Last 30 minutes'),
              },
              {
                id: 2,
                number: 1,
                type: 'hour',
                name: translate('Last 1 hour'),
              },
              {
                id: 3,
                number: 24,
                type: 'hour',
                name: translate('Last 24 hours'),
              },
              { id: 4, number: 7, type: 'day', name: translate('Last 7 days') },
              {
                id: 5,
                number: 1,
                type: 'month',
                name: translate('Last month'),
              },
              { id: 6, number: 1, type: 'year', name: translate('Last year') },
            ]}
            sxPeriod={{ width: '50%', pt: 1, pl: 1 }}
            xsPeriod={2}
            xsDateBetween={2}
            requiredStartDate
          />
          <Grid item xs={2} sx={{ pt: 1, pl: 1 }}>
            <FilterAutocompleteMultipleStatus
              disabled={lockFields}
              onStatusChange={(status) => {
                statusIds?.set(status)
              }}
              statusId={statusIds.get()}
            />
          </Grid>
          <Grid item xs={2} sx={{ pt: 1, pl: 1 }}>
            <FilterAutocompleteClosedStatusClass
              disabled={closedStatusClassLock}
              closedStatusClassId={closedStatusClassId?.get()}
              onClosedStatusClassChange={(closedStatusClass) => {
                closedStatusClassId?.set(closedStatusClass)
                closedStatusId?.set(null)
              }}
            />
          </Grid>
          <Grid item xs={2} sx={{ pt: 1, pl: 1 }}>
            <FilterAutocompleteClosedStatus
              disabled={closedStatusClassLock}
              closedStatusClassId={closedStatusClassId?.get()}
              closedStatusId={closedStatusId?.get()}
              onClosedStatusIdChange={(closedStatus) => {
                closedStatusId?.set(closedStatus)
              }}
            />
          </Grid>
          <Grid item xs={2} sx={{ pt: 1, pl: 1 }}>
            <FilterCheckboxQualifiers
              qualifiersSelected={qualifiersSelected}
              clearState={clearState?.get()}
            />
          </Grid>
          {systemConfig().displayConsummatedFilterInDashboardAndList.get() ===
          '1' ? (
            <Grid item xs={2} sx={{ pt: 1, pl: 1 }}>
              <SelectConsummatedAndAttempted
                disabled={lockFields}
                valueDefault={consummatedAndAttempted.get() ?? ''}
                onReportConsummatedAndAttempetdChange={(type) => {
                  consummatedAndAttempted.set(type)
                }}
                customOption={[
                  { index: 0, value: '0', label: 'Does not apply' },
                  { index: 1, value: '1', label: 'attempted' },
                  { index: 2, value: '2', label: 'consummated' },
                ]}
              />
            </Grid>
          ) : (
            false
          )}
          <Grid item xs={12} container>
            <FilterTimeInterval
              enabled={timeIntervalEnabled}
              startTime={startTime}
              endTime={endTime}
              disabled={lockFields}
            />
            <Grid item xs={2} sx={{ pl: 2, pt: 1 }}>
              <FilterSwitchCoordination
                onCheckedChange={(checked) => {
                  setCoordinationDates(checked)
                }}
                checked={!!currentCoordination.get()}
                onCoordinationChange={(coordination) => {
                  currentCoordination.set(coordination)
                }}
              />
            </Grid>

            <Grid item xs={4} sx={{ pl: 2, pt: 1 }}>
              <FilterSwitchOtherDispatchsGroup
                onCheckedChange={() =>
                  withSolvedThirdParty.set(!withSolvedThirdParty.value)
                }
                disabled={lockFields}
                checked={withSolvedThirdParty.get()}
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid
          container
          direction={'row'}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'end',
            pt: 2,
            gap: 2,
          }}
        >
          <Button variant="outlined" onClick={clearFilters}>
            {translate('Clear Filters')}
          </Button>
          <LoadingButton
            loading={loading.get()}
            variant="contained"
            onClick={searchButton}
            disabled={disabledSendButton()}
          >
            {translate('Search')}
          </LoadingButton>
        </Grid>
      </Grid>
    </Collapse>
  )
}
